/**
 * Slugs for marketing posts.
 *
 * Post titles are Bulgarian but slugs are part of the public URL and must be
 * lowercase latin — the API rejects anything else. So a slug cannot simply be
 * the lowercased title: it has to be transliterated first, or every new post
 * would come back 400 and Gloria would have to invent latin slugs by hand.
 *
 * The map mirrors `transliterateToLatin` in my-pet-api's deeplService. It is
 * duplicated rather than shared because the two live in different repos; if one
 * changes, the mismatch shows up as a slug that differs from the API's, not as
 * a failure — so keep them in step.
 */
const CYRILLIC_TO_LATIN = {
  а: "a",
  б: "b",
  в: "v",
  г: "g",
  д: "d",
  е: "e",
  ж: "zh",
  з: "z",
  и: "i",
  й: "y",
  к: "k",
  л: "l",
  м: "m",
  н: "n",
  о: "o",
  п: "p",
  р: "r",
  с: "s",
  т: "t",
  у: "u",
  ф: "f",
  х: "h",
  ц: "ts",
  ч: "ch",
  ш: "sh",
  щ: "sht",
  ъ: "a",
  ь: "y",
  ю: "yu",
  я: "ya",
};

export const transliterate = (text) =>
  (text || "")
    .split("")
    .map((char) => {
      const lower = char.toLowerCase();
      return CYRILLIC_TO_LATIN[lower] !== undefined ? CYRILLIC_TO_LATIN[lower] : char;
    })
    .join("");

/**
 * Title → slug, in the exact shape the API's pattern accepts: lowercase latin
 * words separated by single hyphens, no leading or trailing hyphen.
 */
export const slugify = (text) =>
  transliterate(text)
    .toLowerCase()
    // Anything that is not a latin letter or digit becomes a separator —
    // punctuation, spaces, and any character the map did not cover.
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Does this slug satisfy the API's rule? Used to warn before saving, not to block typing. */
export const isValidSlug = (slug) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug || "");
